import React from 'react';
import { FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import { ServiceArea, ForemanArea } from '../../../services/areasService';
import FiltersCard from '../../../components/FiltersCard';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import type { AreaFilterValues } from '../utils/areaFilters';

interface AreasFiltersProps {
  serviceAreas: ServiceArea[];
  foremanAreas: ForemanArea[];
  /** Controlled: the page owns the values so a relationship link can set them (W-M22). */
  value: AreaFilterValues;
  onFilterChange: (filters: AreaFilterValues) => void;
  /** The page's search, in the panel's header bar (V2-077). */
  search: SearchFieldProps;
  /** The applied search text: counts as a filter, and Clear All clears it. */
  appliedSearch: string;
  /** Clear All: filters and search back to the defaults. */
  onClear: () => void;
}

const AreasFilters: React.FC<AreasFiltersProps> = ({
  serviceAreas,
  foremanAreas,
  value,
  onFilterChange,
  search,
  appliedSearch,
  onClear,
}) => {
  const handleFilterChange = (field: keyof AreaFilterValues, next: string) => {
    onFilterChange({ ...value, [field]: next });
  };

  // Areas have no inactive state, so no default hides any (V2-075: no change). The search is
  // not counted (it sits beside the chip) but Clear All clears it too.
  const appliedFilterCount = [value.serviceArea !== '', value.foremanArea !== ''].filter(Boolean).length;
  const hasActiveFilters = appliedFilterCount > 0 || appliedSearch !== '';

  return (
    <FiltersCard
      title="Area Filters"
      subtitle="Filter by service area and foreman area"
      collapsible={true}
      defaultCollapsed={false}
      showClearButton={true}
      onClear={onClear}
      hasActiveFilters={hasActiveFilters}
      appliedFilterCount={appliedFilterCount}
      search={search}
    >
      <FilterGrid min="lg">
        <FilterField>
          <StandoutDropdown<ServiceArea>
            type="searchable"
            options={serviceAreas}
            value={value.serviceArea}
            onChange={(v) => handleFilterChange('serviceArea', v)}
            getValue={(area) => area.name}
            formatOption={(area) => `${area.name}${area.code ? ` (${area.code})` : ''}`}
            placeholder="All Service Areas"
            className="tw-w-full"
            label="Service Area"
            noOptionsMessage="No service areas available"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown<ForemanArea>
            type="searchable"
            options={foremanAreas}
            value={value.foremanArea}
            onChange={(v) => handleFilterChange('foremanArea', v)}
            getValue={(area) => area.name}
            formatOption={(area) => `${area.name}${area.code ? ` (${area.code})` : ''}`}
            placeholder="All Foreman Areas"
            className="tw-w-full"
            label="Foreman Area"
            noOptionsMessage="No foreman areas available"
          />
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default AreasFilters;
